import { forwardRef, RefObject, useEffect, useState } from 'react';
import { Button, Portal, SimpleGrid } from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';

import { useCreateCompanyStore } from 'app/store';
import useCreateCompany from 'app/hooks/useCreateCompany';
import {
  useToast,
  Company,
  ArrowLeftOutline,
  ModulesEnableOptions,
} from '@aslanpay/portal-common-lib';

import {
  CompanyDetailsFormFields,
  CompanyModulesFormFields,
  CompanyFormProps,
  MODULE_DISPLAY_INFO,
} from '../types';

import Module from './Module';

const supportsProvisioning = (module: string) =>
  Boolean(MODULE_DISPLAY_INFO[module]?.provisionBeforeEnable);

const getDefaultStatusForSelectedModule = (module: string): ModulesEnableOptions =>
  supportsProvisioning(module) ? 'DISABLED' : 'ENABLED';

const getCreateCompanyModuleStatus = (
  module: string,
  status: ModulesEnableOptions
): ModulesEnableOptions => {
  if (!supportsProvisioning(module) && status === 'DISABLED') {
    return 'UNAVAILABLE';
  }

  return status;
};

export const getDefaultCreateCompanyModuleStatuses = (
  allModuleKeys: string[]
): Record<string, ModulesEnableOptions> =>
  Object.fromEntries(
    allModuleKeys.map((module) => [module, getDefaultStatusForSelectedModule(module)])
  );

export const getInitialCreateCompanyModuleStatuses = (
  allModuleKeys: string[],
  storedModules: CompanyModulesFormFields['modules'],
  storedModuleStatuses: CompanyModulesFormFields['moduleStatuses']
): Record<string, ModulesEnableOptions> => {
  if (storedModuleStatuses) {
    const defaultStatuses = getDefaultCreateCompanyModuleStatuses(allModuleKeys);

    return Object.fromEntries(
      allModuleKeys.map((module) => [
        module,
        getCreateCompanyModuleStatus(
          module,
          storedModuleStatuses[module] ?? defaultStatuses[module]
        ),
      ])
    );
  }

  if (storedModules) {
    return Object.fromEntries(
      allModuleKeys.map((module) => [
        module,
        storedModules.includes(module) ? getDefaultStatusForSelectedModule(module) : 'UNAVAILABLE',
      ])
    );
  }

  return getDefaultCreateCompanyModuleStatuses(allModuleKeys);
};

const CompanyModulesForm = forwardRef<HTMLElement, CompanyFormProps>(
  ({ id, onStepAction, previousStep, canPrevStep }, ref) => {
    const toast = useToast();
    const queryClient = useQueryClient();

    const companyData = useCreateCompanyStore((state) => state.companyData);
    const resetCompanyData = useCreateCompanyStore((state) => state.resetCompanyData);
    const setCompanyData = useCreateCompanyStore((state) => state.setCompanyData);

    const allModuleKeys = Object.keys(MODULE_DISPLAY_INFO);

    const [moduleStatuses, setModuleStatuses] = useState<Record<string, ModulesEnableOptions>>(
      getInitialCreateCompanyModuleStatuses(
        allModuleKeys,
        (companyData as CompanyModulesFormFields).modules,
        (companyData as CompanyModulesFormFields).moduleStatuses
      )
    );
    const [isModulesValid, setIsModulesValid] = useState(true);

    const { mutate: createCompany, isLoading: isCreatingCompany } = useCreateCompany({
      onSuccess: (company: Company) => {
        toast({
          position: 'top-right',
          status: 'success',
          title: 'New company created',
          description: `${company.name} was successfully created!`,
        });

        queryClient.invalidateQueries({ queryKey: ['fetchCompaniesPage'] });

        resetCompanyData();

        onStepAction();
      },
      onError: () => {
        toast({
          position: 'top-right',
          status: 'error',
          title: 'Something went wrong',
          description: 'Sorry, the new company was not created. Please try again.',
        });
      },
    });

    const handleCreateCompanySubmit = () => {
      const {
        name,
        displayName,
        countryCode,
        companyNumber,
        feeGroup,
        addressLine1,
        addressLine2,
        city,
        postcode,
        type,
        partnerId,
      } = companyData as CompanyDetailsFormFields;

      createCompany({
        name,
        displayName: displayName || undefined,
        countryCode,
        companyNumber,
        feeGroup: feeGroup || 'DEFAULT_FEE',
        address: {
          addressLine1,
          addressLine2,
          postCode: postcode,
          city,
          countryCode,
        },
        companyConfig: {
          modulesConfig: {
            modules: moduleStatuses,
          },
        },
        type,
        partnerId: partnerId || undefined,
      });
    };

    const updateModuleStatus = (module: string) => (status: ModulesEnableOptions) => {
      setModuleStatuses((previous) => ({
        ...previous,
        [module]: getCreateCompanyModuleStatus(module, status),
      }));
    };

    const handleBackCLick = () => {
      const formValues = companyData as CompanyModulesFormFields;
      formValues.moduleStatuses = moduleStatuses;
      setCompanyData(formValues);
      previousStep();
    };

    useEffect(() => {
      if (Object.values(moduleStatuses).every((status) => status === 'UNAVAILABLE')) {
        setIsModulesValid(false);
      } else {
        setIsModulesValid(true);
      }
    }, [moduleStatuses]);

    return (
      <>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing="12px">
          {allModuleKeys.map((module) => (
            <Module
              key={module}
              isChecked={moduleStatuses[module] === 'ENABLED'}
              isForm
              module={module}
              status={moduleStatuses[module]}
              onStatusChange={updateModuleStatus(module)}
              supportsProvisioning={supportsProvisioning(module)}
            />
          ))}
        </SimpleGrid>
        <Portal containerRef={ref as RefObject<HTMLElement>}>
          <Button
            size="sm"
            variant="outline"
            isDisabled={!canPrevStep}
            leftIcon={<ArrowLeftOutline boxSize="20px" />}
            onClick={handleBackCLick}
            mr="16px"
          >
            Back
          </Button>
          <Button
            onClick={handleCreateCompanySubmit}
            form={id}
            type="submit"
            size="sm"
            isLoading={isCreatingCompany}
            isDisabled={!isModulesValid}
          >
            Create
          </Button>
        </Portal>
      </>
    );
  }
);

CompanyModulesForm.displayName = 'CompanyModulesForm';

export default CompanyModulesForm;
